import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { getPatResults, getPatStudentItems } from '../../../../services/analytics/analytics.service'
import Loading from '../../../Loading/Loading'
import DataAsOf from './DataAsOf'

// Réplica del ACER Student report: scatter posición × dificultad, color por resultado,
// línea del scale score, + tabla de ítems. MF-508.
const C_BAR = '#2b5784'
const C_CORRECT = '#2e9e68'
const C_INCORRECT = '#c0392b'
const C_MISSED = '#cfd6e0'

const SCALES = [
  { value: 'pat-maths-scale', label: 'PAT Maths' },
  { value: 'pat-reading-scale', label: 'PAT Reading' }
]

const isAnswered = (s) => s === 'attempted' || s === 'answered'

const markerColor = (it) => {
  if (!isAnswered(it.response_state)) return C_MISSED
  return it.is_correct ? C_CORRECT : C_INCORRECT
}

const sittingLabel = (r) => {
  const name = [r.last_name, r.first_name].filter(Boolean).join(', ') || r.candidate_unique_id
  const yl = r.year_level ? `Yr ${r.year_level}` : ''
  const score = r.scale_score != null ? Number(r.scale_score).toFixed(1) : '—'
  return `${name} · ${yl} · ${r.sitting_year || ''} (${score})`
}

const Toggle = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    {SCALES.map((s) => (
      <button
        key={s.value}
        onClick={() => onChange(s.value)}
        style={{
          fontSize: 12,
          fontWeight: 600,
          padding: '5px 11px',
          borderRadius: 7,
          cursor: 'pointer',
          border: '1px solid ' + (value === s.value ? C_BAR : '#d5dbe4'),
          background: value === s.value ? C_BAR : '#fff',
          color: value === s.value ? '#fff' : '#5a6472'
        }}
      >
        {s.label}
      </button>
    ))}
  </div>
)

const buildOption = (items, scaleScore) => {
  const points = items
    .filter((it) => it.difficulty != null)
    .map((it) => ({
      value: [it.position, Number(it.difficulty)],
      itemStyle: { color: markerColor(it) },
      meta: it
    }))

  const series = [
    {
      type: 'scatter',
      symbolSize: 11,
      data: points,
      markLine:
        scaleScore != null
          ? {
              silent: true,
              symbol: 'none',
              lineStyle: { color: '#e0a100', type: 'dashed', width: 1.5 },
              label: {
                formatter: `Scale score ${Number(scaleScore).toFixed(1)}`,
                fontSize: 10,
                color: '#b5820a',
                position: 'insideEndTop'
              },
              data: [{ yAxis: Number(scaleScore) }]
            }
          : undefined
    }
  ]

  return {
    backgroundColor: 'transparent',
    grid: { left: 52, right: 24, top: 20, bottom: 44 },
    xAxis: {
      type: 'value',
      name: 'Position within test',
      nameLocation: 'middle',
      nameGap: 26,
      nameTextStyle: { fontSize: 11, color: '#888' },
      minInterval: 1,
      axisLabel: { fontSize: 10, color: '#aaa' },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'value',
      scale: true,
      name: 'Difficulty / scale',
      nameTextStyle: { fontSize: 10, color: '#aaa', align: 'left' },
      axisLabel: { fontSize: 10, color: '#aaa' },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series,
    tooltip: {
      trigger: 'item',
      formatter: (p) => {
        const it = p.data.meta || {}
        const res = !isAnswered(it.response_state)
          ? it.response_state || 'not seen'
          : it.is_correct
            ? 'Correct'
            : 'Incorrect'
        return `
          <b>Position ${it.position}</b><br/>
          Difficulty: ${it.difficulty ?? '—'}<br/>
          Strand: ${it.strand || '—'}<br/>
          Sub-strand: ${it.sub_strand || '—'}<br/>
          ${it.description ? it.description + '<br/>' : ''}
          Result: <b>${res}</b>${it.response_key ? ' (' + it.response_key + ')' : ''}
        `
      }
    }
  }
}

const Legend = () => (
  <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
    {[
      ['Correct', C_CORRECT],
      ['Incorrect', C_INCORRECT],
      ['Not seen / no response', C_MISSED]
    ].map(([label, color]) => (
      <span
        key={label}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#666' }}
      >
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
        {label}
      </span>
    ))}
  </div>
)

const ItemTable = ({ items }) => (
  <div style={{ marginTop: 20, overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: '#5a6472', borderBottom: '2px solid #e6eaf0' }}>
          {['#', 'Result', 'Strand', 'Sub-strand', 'Description', 'Difficulty'].map((h) => (
            <th key={h} style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((it) => {
          const answered = isAnswered(it.response_state)
          return (
            <tr key={it.item_uid + '-' + it.position} style={{ borderBottom: '1px solid #f0f2f6' }}>
              <td style={{ padding: '6px 10px', color: '#98a2b3' }}>{it.position}</td>
              <td style={{ padding: '6px 10px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    minWidth: 18,
                    textAlign: 'center',
                    fontWeight: 700,
                    color: !answered ? '#98a2b3' : it.is_correct ? C_CORRECT : C_INCORRECT
                  }}
                >
                  {!answered ? '–' : it.is_correct ? '✓' : '✗'}
                </span>
                {it.response_key ? (
                  <span style={{ color: '#98a2b3', marginLeft: 4 }}>{it.response_key}</span>
                ) : null}
              </td>
              <td style={{ padding: '6px 10px' }}>{it.strand || '—'}</td>
              <td style={{ padding: '6px 10px' }}>{it.sub_strand || '—'}</td>
              <td style={{ padding: '6px 10px', color: '#5a6472' }}>{it.description || '—'}</td>
              <td style={{ padding: '6px 10px', fontVariantNumeric: 'tabular-nums' }}>
                {it.difficulty ?? '—'}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  </div>
)

const Kpi = ({ label, value }) => (
  <div style={{ minWidth: 90 }}>
    <div style={{ fontSize: 10, color: '#98a2b3', textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {label}
    </div>
    <div style={{ fontSize: 16, fontWeight: 700, color: C_BAR }}>{value}</div>
  </div>
)

const PatStudentReport = () => {
  const [scale, setScale] = useState('pat-maths-scale')
  const [sittings, setSittings] = useState([])
  const [sittingId, setSittingId] = useState('')
  const [items, setItems] = useState([])
  const [year, setYear] = useState('')
  const [search, setSearch] = useState('')
  const [loadingList, setLoadingList] = useState(true)
  const [loadingItems, setLoadingItems] = useState(false)
  const [error, setError] = useState(null)

  // Lista de sittings (picker de alumno) por escala.
  useEffect(() => {
    setLoadingList(true)
    setSittingId('')
    setItems([])
    setYear('')
    setSearch('')
    getPatResults({ scaleSlug: scale })
      .then((res) => setSittings(res?.data || []))
      .catch((err) => setError(err?.message || 'Error loading sittings'))
      .finally(() => setLoadingList(false))
  }, [scale])

  // Años disponibles (para el filtro) — con 3000+ sittings el selector es inusable
  // sin acotar por año + nombre.
  const years = useMemo(
    () => [...new Set(sittings.map((s) => s.sitting_year).filter(Boolean))].sort((a, b) => b - a),
    [sittings]
  )

  // Sittings filtrados por año + búsqueda de nombre, ordenados por apellido.
  const filteredSittings = useMemo(() => {
    const q = search.trim().toLowerCase()
    return sittings
      .filter((s) => (year ? String(s.sitting_year) === String(year) : true))
      .filter((s) => (q ? `${s.last_name || ''} ${s.first_name || ''}`.toLowerCase().includes(q) : true))
      .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''))
  }, [sittings, year, search])

  // Si el sitting seleccionado deja de estar en el filtro, lo limpiamos.
  useEffect(() => {
    if (sittingId && !filteredSittings.some((s) => s.sitting_id === sittingId)) {
      setSittingId('')
      setItems([])
    }
  }, [filteredSittings, sittingId])

  // Ítems del sitting seleccionado.
  useEffect(() => {
    if (!sittingId) return
    setLoadingItems(true)
    getPatStudentItems({ sittingId })
      .then((res) => setItems(res?.data || []))
      .catch((err) => setError(err?.message || 'Error loading items'))
      .finally(() => setLoadingItems(false))
  }, [sittingId])

  const selected = useMemo(() => sittings.find((s) => s.sitting_id === sittingId), [sittings, sittingId])
  const option = useMemo(() => buildOption(items, selected?.scale_score), [items, selected])

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap'
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C_BAR }}>
            Student report — item detail
          </h2>
          <DataAsOf />
        </div>
        <Toggle value={scale} onChange={setScale} />
      </div>

      {loadingList ? (
        <Loading />
      ) : error ? (
        <div style={{ padding: 24, color: '#c0392b' }}>
          <strong>Error:</strong> {error}
        </div>
      ) : !sittings.length ? (
        <p style={{ color: '#888', padding: 24 }}>
          No {SCALES.find((s) => s.value === scale)?.label} sittings with item detail yet — run a sync and
          ingest first.
        </p>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
              marginBottom: 16
            }}
          >
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #d5dbe4',
                fontSize: 13,
                color: '#344054'
              }}
            >
              <option value=''>All years</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <input
              type='text'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search student…'
              style={{
                flex: '1 1 180px',
                minWidth: 160,
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #d5dbe4',
                fontSize: 13,
                color: '#344054'
              }}
            />

            <select
              value={sittingId}
              onChange={(e) => setSittingId(e.target.value)}
              style={{
                flex: '2 1 320px',
                minWidth: 260,
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #d5dbe4',
                fontSize: 13,
                color: '#344054'
              }}
            >
              <option value=''>
                {filteredSittings.length
                  ? `Select a student sitting… (${filteredSittings.length})`
                  : 'No matches'}
              </option>
              {filteredSittings.map((s) => (
                <option key={s.sitting_id} value={s.sitting_id}>
                  {sittingLabel(s)}
                </option>
              ))}
            </select>
          </div>

          {!sittingId ? (
            <p style={{ color: '#98a2b3', padding: '12px 0' }}>
              Pick a student above to see their per-item scatter and table.
            </p>
          ) : loadingItems ? (
            <Loading />
          ) : !items.length ? (
            <p style={{ color: '#888', padding: 12 }}>No item-level detail stored for this sitting.</p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 8 }}>
                <Kpi
                  label='Student'
                  value={[selected?.first_name, selected?.last_name].filter(Boolean).join(' ') || '—'}
                />
                <Kpi
                  label='Scale score'
                  value={selected?.scale_score != null ? Number(selected.scale_score).toFixed(1) : '—'}
                />
                <Kpi label='Band' value={selected?.band_key || '—'} />
                <Kpi label='Year level' value={selected?.year_level || '—'} />
                <Kpi label='Items' value={items.length} />
              </div>
              <ReactECharts
                option={option}
                style={{ height: 360, width: '100%' }}
                opts={{ renderer: 'canvas' }}
                notMerge
              />
              <Legend />
              <ItemTable items={items} />
            </>
          )}
        </>
      )}
    </div>
  )
}

export default PatStudentReport
